import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import SchoolServiceAdmin from '../services/SchoolServiceAdmin';
import UploadExcel from './AfterLoginPages/UploadExcel';
import CreateDeleteAbsenceListForTeacher from './AfterLoginPages/CreateDeleteAbsenceListForTeacher';
import ArchivedStudentList from './AfterLoginPages/ArchivedStudentsList';
import booksImage from '../imgs/books.jpg';
import holidaysImage from '../imgs/holiday.jpg';
import sapImage from '../imgs/sap.jpg';
import StudentsTable from './superadmin/StudentsTable';
import {
  CalendarDaysIcon,
  DocumentChartBarIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
} from '@heroicons/react/24/solid';
import Select from 'react-select';
import { hasSpacingIssue } from '../utils/textUtils';

const SchoolSuperAdmin = ({ data, setData }) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [allStudents, setAllStudents] = useState(null);
  const [allTeachers, setAllTeachers] = useState(null);
  const [allArchivedStudents, setAllArchivedStudents] =
    useState(null);

  const [isStudentsTableVisible, setIsStudentsTableVisible] =
    useState(false);
  const [isTeachersTableVisible, setIsTeachersTableVisible] =
    useState(false);

  const [isEmailSendingEnabled, setIsEmailSendingEnabled] =
    useState(false);

  const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const [showDaysModal, setShowDaysModal] = useState(false);
  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedTeacherId, setSelectedTeacherId] = useState(null);

  //for teacher
  const [editingEnabled, setEditingEnabled] = useState(false);
  const [editingTeacherId, setEditingTeacherId] = useState(null);
  const [editingField, setEditingField] = useState('');
  const [editingValue, setEditingValue] = useState('');
  const [updateMessage, setUpdateMessage] = useState('');

  // Student Report States
  const [reportStudentId, setReportStudentId] = useState('');
  const [reportFromDate, setReportFromDate] = useState('');
  const [reportToDate, setReportToDate] = useState('');
  const [reportLoading, setReportLoading] = useState(false);
  const [reportMessage, setReportMessage] = useState('');
  const [reportMessageType, setReportMessageType] = useState('');

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordChangeMessage, setPasswordChangeMessage] =
    useState('');
  const [passwordChangeType, setPasswordChangeType] = useState('');
  const [passwordChangeLoading, setPasswordChangeLoading] =
    useState(false);

  // All Students Report States
  const [allStudentsReportFromDate, setAllStudentsReportFromDate] =
    useState('');
  const [allStudentsReportToDate, setAllStudentsReportToDate] =
    useState('');
  const [allStudentsReportLoading, setAllStudentsReportLoading] =
    useState(false);
  const [allStudentsReportMessage, setAllStudentsReportMessage] =
    useState('');
  const [
    allStudentsReportMessageType,
    setAllStudentsReportMessageType,
  ] = useState('');

  const [studentSearchTerm, setStudentSearchTerm] = useState('');

  // Check if user is a SUPER_ADMIN
  const isSuperAdmin = data.roles?.some(
    (role) => role.roleCode === 'SUPER_ADMIN',
  );

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!data || !isSuperAdmin) {
      localStorage.clear();
      navigate('/');
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const [students, archivedStudents, teachers, emailStatus] =
          await Promise.all([
            SchoolServiceAdmin.getAllStudents(),
            SchoolServiceAdmin.getAllArchivedStudents(),
            SchoolServiceAdmin.getAllTeachers(),
            SchoolServiceAdmin.getEmailSendingStatus(),
          ]);

        setAllStudents(students.data);
        setAllArchivedStudents(archivedStudents.data);
        setAllTeachers(teachers.data);

        setIsEmailSendingEnabled(emailStatus.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
      setLoading(false);
    };
    fetchData();
  }, [data, isSuperAdmin, navigate]);

  const refreshAllStudents = async () => {
    try {
      const response = await SchoolServiceAdmin.getAllStudents();
      setAllStudents(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const refreshAllTeacher = async () => {
    setLoading(true);
    try {
      const response2 = await SchoolServiceAdmin.getAllTeachers();
      setAllTeachers(response2.data);
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  const toggleStudentsTableVisibility = () => {
    setIsStudentsTableVisible(!isStudentsTableVisible);
  };
  const toggleTeachersTableVisibility = () => {
    setIsTeachersTableVisible(!isTeachersTableVisible);
  };

  const handleCheckboxChange = async (event) => {
    const isChecked = event.target.checked;
    try {
      await SchoolServiceAdmin.updateSendEmailFlag(isChecked);
      setIsEmailSendingEnabled(isChecked);
    } catch (error) {
      console.error('Error updating send email flag:', error);
    }
  };

  const fetchArchivedStudents = async () => {
    try {
      const response =
        await SchoolServiceAdmin.getAllArchivedStudents();
      setAllArchivedStudents(response.data);
    } catch (error) {
      console.error('Error fetching archived students:', error);
    }
  };

  const handleGenerateStudentReport = async () => {
    if (!isEmailSendingEnabled) {
      setReportMessage(
        'Email sending is disabled. Please enable it before generating the report.',
      );
      setReportMessageType('error');
      setTimeout(() => {
        setReportMessage('');
        setReportMessageType('');
      }, 4000);
      return; // stop execution
    }

    if (!reportStudentId || !reportFromDate || !reportToDate) {
      setReportMessage('Please fill in all fields');
      setReportMessageType('error');
      setTimeout(() => {
        setReportMessage('');
        setReportMessageType('');
      }, 4000); // message disappears after 4 seconds
      return;
    }

    setReportLoading(true);
    setReportMessage('');
    try {
      const response = await SchoolServiceAdmin.generateStudentReport(
        reportStudentId,
        reportFromDate,
        reportToDate,
      );
      setReportMessage(
        response.data || 'Report generation initiated',
      );
      setReportMessageType('success');
      setReportStudentId('');
      setReportFromDate('');
      setReportToDate('');
    } catch (error) {
      setReportMessage(
        error.response?.data?.message || 'Failed to generate report',
      );
      setReportMessageType('error');
    } finally {
      setReportLoading(false);
      setTimeout(() => {
        setReportMessage('');
        setReportMessageType('');
      }, 4000); // hide after 4 seconds
    }
  };

  const handleGenerateAllStudentsReport = async () => {
    if (!isEmailSendingEnabled) {
      setAllStudentsReportMessage(
        'Email sending is disabled. Please enable it before generating the report.',
      );
      setAllStudentsReportMessageType('error');
      setTimeout(() => {
        setAllStudentsReportMessage('');
        setAllStudentsReportMessageType('');
      }, 4000);
      return;
    }

    if (!allStudentsReportFromDate || !allStudentsReportToDate) {
      setAllStudentsReportMessage('Please fill in all date fields');
      setAllStudentsReportMessageType('error');
      setTimeout(() => {
        setAllStudentsReportMessage('');
        setAllStudentsReportMessageType('');
      }, 4000);
      return;
    }

    setAllStudentsReportLoading(true);
    setAllStudentsReportMessage('');
    try {
      const response =
        await SchoolServiceAdmin.generateAllStudentsReport(
          allStudentsReportFromDate,
          allStudentsReportToDate,
        );
      setAllStudentsReportMessage(
        response.data ||
          'Report generation initiated and sent to admin email',
      );
      setAllStudentsReportMessageType('success');
      setAllStudentsReportFromDate('');
      setAllStudentsReportToDate('');
    } catch (error) {
      setAllStudentsReportMessage(
        error.response?.data?.message || 'Failed to generate report',
      );
      setAllStudentsReportMessageType('error');
    } finally {
      setAllStudentsReportLoading(false);
      setTimeout(() => {
        setAllStudentsReportMessage('');
        setAllStudentsReportMessageType('');
      }, 4000);
    }
  };

  const getSelectedStudentEmail = () => {
    if (!reportStudentId || !allStudents) return '';
    const student = allStudents.find(
      (s) => s.id === parseInt(reportStudentId),
    );
    return student?.email || '';
  };

  // call this from your component to show next bi-weekly report Sunday
  const getNextBiWeeklyReportDate = () => {
    const START_DATE = new Date(2024, 4, 5); // May 5, 2024 (months are 0-indexed)
    const today = new Date();

    // Normalize time to midnight to avoid timezone / hour issues
    const toMidnight = (d) =>
      new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const start = toMidnight(START_DATE);
    let candidate = toMidnight(today);

    // If today is not Sunday, move candidate forward to the next Sunday.
    // getDay(): 0 = Sunday, 1 = Monday, ... 6 = Saturday
    const daysUntilNextSunday = (7 - candidate.getDay()) % 7;
    if (daysUntilNextSunday !== 0) {
      candidate.setDate(candidate.getDate() + daysUntilNextSunday);
    }
    // If today is Sunday, candidate remains today (we include today)

    const msPerWeek = 7 * 24 * 60 * 60 * 1000;

    // Advance candidate by 7 days until the parity matches the backend rule.
    while (true) {
      // compute whole weeks between START_DATE and candidate (floor like ChronoUnit.WEEKS.between)
      const weeksBetween = Math.floor(
        (candidate - start) / msPerWeek,
      );

      if (weeksBetween % 2 === 0) {
        // candidate is a bi-weekly report Sunday
        return candidate.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
      }

      // otherwise go to the following Sunday (add 7 days)
      candidate = new Date(candidate.getTime() + msPerWeek);
    }
  };

  const getNextMonthlyReportDate = () => {
    const today = new Date();
    const nextMonth = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      1,
    );

    return nextMonth.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="container mx-auto my-8">
      <ArchivedStudentList
        allArchivedStudents={allArchivedStudents}
        loading={loading}
        fetchArchivedStudents={fetchArchivedStudents}
        isSuperAdmin={isSuperAdmin}
      />

      <div className="inline-flex items-center justify-center w-full">
        <hr className="w-96 h-1 my-8 bg-cyan-800 border-0 rounded " />
        <div className="absolute px-4 -translate-x-1/2 bg-white left-1/2 space-x-2">
          <button
            onClick={toggleStudentsTableVisibility}
            data-testid="toggle-students-table"
            className="rounded bg-cyan-700 text-white px-6 py-2 font-semibold hover:bg-cyan-800"
          >
            {isStudentsTableVisible
              ? 'Hide All Students'
              : 'Show All Students'}
          </button>
          <button
            onClick={() => {
              navigate('/superadmin/createstudent');
            }}
            className="rounded bg-green-700 text-white px-6 py-2 font-semibold hover:bg-green-800"
          >
            + Add New Student
          </button>
        </div>
      </div>

      {!loading && isStudentsTableVisible && (
        <StudentsTable
          data-testid="students-table"
          allStudents={allStudents}
          refreshAllStudents={refreshAllStudents}
        />
      )}
      <hr className="w-960 h-2  mx-auto my-4 bg-cyan-800 border-0 rounded md:my-10 dark:bg-gray-700" />

      <div className="inline-flex items-center justify-center w-full">
        <hr className="w-96 h-1 my-8 bg-cyan-800 border-0 rounded " />
        <div className="absolute px-4 -translate-x-1/2 bg-white left-1/2 space-x-2">
          <button
            onClick={toggleTeachersTableVisibility}
            data-testid="toggle-teachers-table"
            className="rounded bg-cyan-700 text-white px-6 py-2 font-semibold hover:bg-cyan-800"
          >
            {isTeachersTableVisible
              ? 'Hide All Teachers'
              : 'Show All Teachers'}
          </button>
          <button
            onClick={() => {
              navigate('/superadmin/createuser');
            }}
            className="rounded bg-green-700 text-white px-6 py-2 font-semibold hover:bg-green-800"
          >
            + Add New Teacher
          </button>
        </div>
      </div>

      {!loading && isTeachersTableVisible && (
        <div
          data-testid="teachers-table"
          className="w-full lg:flex-wrap lg:flex md:flex-1 my-4"
        >
          <div className="flex items-center justify-center w-full mb-8">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={editingEnabled}
                onChange={(e) => setEditingEnabled(e.target.checked)}
              />
              <div
                className="
        w-11 h-6 
        bg-gray-300 
        rounded-full 
        peer 
        peer-checked:bg-blue-500 
        peer-focus:ring-2 
        peer-focus:ring-blue-300 
        transition-all duration-300
      "
              ></div>
              <div
                className="
        absolute left-0.5 top-0.5 
        w-5 h-5 
        bg-white 
        rounded-full 
        transition-all duration-300 
        peer-checked:translate-x-5 
        peer-checked:scale-110
        peer-checked:shadow-md
      "
              ></div>
              <span className="ml-3 text-gray-800 font-medium select-none">
                Enable Editing
              </span>
            </label>
          </div>
          <div className="w-full overflow-x-auto">
            <table className="w-full border rounded overflow-hidden table-auto">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="py-2 px-4">Teacher ID</th>
                  <th className="py-2 px-4">Email</th>
                  <th className="py-2 px-4">First Name</th>
                  <th className="py-2 px-4">Last Name</th>
                  <th className="py-2 px-4">User Name</th>
                  <th className="py-2 px-4">Class Abbrev</th>
                  <th className="py-2 px-4">Class Days of Week</th>
                  <th className="py-2 px-4">Class Unique Number</th>
                  <th className="py-2 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {allTeachers?.map((myTeacher) => (
                  <tr
                    key={myTeacher.id}
                    className="border-b text-center hover:bg-gray-50"
                  >
                    <td className="py-2 px-2">{myTeacher.id}</td>

                    {[
                      'email',
                      'firstName',
                      'lastName',
                      'username',
                      'classAbbrev',
                      'teacherClassDaysOfWeek',
                      'classUniqueNumber',
                    ].map((field) => {
                      const value = myTeacher[field];

                      // ✅ validation logic
                      let hasIssue = false;

                      if (
                        [
                          'email',
                          'firstName',
                          'lastName',
                          'username',
                          'classAbbrev',
                        ].includes(field)
                      ) {
                        hasIssue = hasSpacingIssue(value);
                      }
                      return (
                        <td
                          key={field}
                          className={`py-2 px-2 ${
                            editingEnabled
                              ? 'cursor-pointer hover:bg-blue-50'
                              : ''
                          } ${hasIssue ? 'bg-yellow-200 text-red-700 font-semibold' : ''}`}
                          onClick={() => {
                            if (!editingEnabled) return;

                            if (field === 'teacherClassDaysOfWeek') {
                              setSelectedTeacherId(myTeacher.id);
                              setSelectedDays(
                                myTeacher.teacherClassDaysOfWeek ||
                                  [],
                              );
                              setShowDaysModal(true);
                              return;
                            }

                            setEditingTeacherId(myTeacher.id);
                            setEditingField(field);
                            setEditingValue(myTeacher[field] || '');
                          }}
                        >
                          {editingTeacherId === myTeacher.id &&
                          editingField === field ? (
                            <input
                              autoFocus
                              value={editingValue}
                              onChange={(e) =>
                                setEditingValue(e.target.value)
                              }
                              onBlur={async () => {
                                const confirmed = window.confirm(
                                  `Are you sure you want to update ${field}?`,
                                );
                                if (!confirmed) {
                                  setEditingTeacherId(null);
                                  setEditingField('');
                                  return;
                                }

                                try {
                                  await SchoolServiceAdmin.updateTeacherField(
                                    myTeacher.id,
                                    field,
                                    editingValue,
                                  );
                                  setUpdateMessage(
                                    `${field} updated successfully`,
                                  );
                                  refreshAllTeacher();
                                } catch (error) {
                                  setUpdateMessage(
                                    error.response?.data?.message ||
                                      'Update failed',
                                  );
                                }

                                setTimeout(
                                  () => setUpdateMessage(''),
                                  3000,
                                );
                                setEditingTeacherId(null);
                                setEditingField('');
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter')
                                  e.target.blur();
                              }}
                              className="border border-blue-400 bg-blue-50 rounded px-2 py-1 w-full focus:ring focus:ring-blue-300"
                            />
                          ) : field === 'teacherClassDaysOfWeek' ? (
                            (
                              myTeacher.teacherClassDaysOfWeek || []
                            ).join(', ')
                          ) : (
                            myTeacher[field]
                          )}
                        </td>
                      );
                    })}

                    <td className="py-2 px-4 space-x-2">
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        <button
                          onClick={() =>
                            navigate(
                              `/superadmin/updatestudentgroup/${myTeacher.id}`,
                            )
                          }
                          className="rounded bg-cyan-700 text-white px-6 py-2 font-semibold hover:bg-cyan-800"
                        >
                          update group
                        </button>

                        <button
                          onClick={() => {
                            setSelectedTeacher(myTeacher);
                            setShowPasswordModal(true);
                          }}
                          className="rounded bg-orange-600 text-white px-6 py-2 font-semibold hover:bg-orange-700"
                        >
                          change password
                        </button>

                        <button
                          onClick={async () => {
                            const confirmed = window.confirm(
                              `Are you sure you want to delete teacher ${myTeacher.firstName} ${myTeacher.lastName}?`,
                            );
                            if (!confirmed) return;
                            try {
                              await SchoolServiceAdmin.deleteTeacher(
                                myTeacher.id,
                              );
                              setUpdateMessage(
                                'Success: Teacher deleted successfully.',
                              );
                              refreshAllTeacher();
                            } catch (error) {
                              const message =
                                'Cannot delete teacher because students are still assigned';

                              setUpdateMessage(message);
                            }
                          }}
                          className="rounded bg-red-700 text-white px-6 py-2 font-semibold hover:bg-red-800"
                        >
                          delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {showDaysModal && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
                onClick={() => {
                  setShowDaysModal(false);
                  setSelectedDays([]);
                  setSelectedTeacherId(null);
                }}
              >
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="
        w-full max-w-md
        bg-white rounded-xl shadow-2xl
        p-6
        animate-[fadeIn_0.2s_ease-out]
      "
                >
                  {/* Header */}
                  <div className="mb-5">
                    <h2 className="text-xl font-semibold text-gray-800">
                      Select Class Days
                    </h2>
                    <p className="text-sm text-gray-500">
                      Choose the days this class meets
                    </p>
                  </div>

                  {/* Days */}
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    {DAYS.map((day) => {
                      const checked = selectedDays.includes(day);

                      return (
                        <label
                          key={day}
                          className={`
                flex items-center gap-3 px-4 py-3 rounded-lg
                border cursor-pointer transition-all
                ${
                  checked
                    ? 'border-blue-500 bg-blue-50 shadow-sm'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }
              `}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedDays([
                                  ...selectedDays,
                                  day,
                                ]);
                              } else {
                                setSelectedDays(
                                  selectedDays.filter(
                                    (d) => d !== day,
                                  ),
                                );
                              }
                            }}
                            className="accent-blue-600 scale-110"
                          />
                          <span className="font-medium text-gray-700">
                            {day}
                          </span>
                        </label>
                      );
                    })}
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-3 pt-2 border-t">
                    <button
                      onClick={() => {
                        setShowDaysModal(false);
                        setSelectedDays([]);
                        setSelectedTeacherId(null);
                      }}
                      className="
            px-4 py-2 rounded-lg
            border border-gray-300
            text-gray-700 font-medium
            hover:bg-gray-100
            transition
          "
                    >
                      Cancel
                    </button>

                    <button
                      onClick={async () => {
                        try {
                          await SchoolServiceAdmin.updateTeacherClassDays(
                            selectedTeacherId,
                            selectedDays,
                          );
                          refreshAllTeacher();
                        } catch (error) {
                          alert(
                            error.response?.data?.message ||
                              'Update failed',
                          );
                        }

                        setShowDaysModal(false);
                        setSelectedDays([]);
                        setSelectedTeacherId(null);
                      }}
                      className="
            px-5 py-2 rounded-lg
            bg-blue-600 text-white font-semibold
            hover:bg-blue-700
            active:scale-95
            transition
          "
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>{' '}
        </div>
      )}

      {updateMessage && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div
            className={`bg-white rounded-lg shadow-lg px-6 py-4 w-80 text-center border ${
              updateMessage.toLowerCase().includes('success')
                ? 'border-green-400'
                : 'border-red-400'
            }`}
          >
            <h3
              className={`text-lg font-semibold mb-2 ${
                updateMessage.toLowerCase().includes('success')
                  ? 'text-green-700'
                  : 'text-red-700'
              }`}
            >
              {updateMessage.toLowerCase().includes('success')
                ? '✅ Success'
                : '⚠️ Error'}
            </h3>
            <p className="text-gray-800">{updateMessage}</p>

            <div className="mt-4">
              <button
                onClick={() => setUpdateMessage('')}
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {showPasswordModal && (
        <div
          data-testid="password-modal"
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
        >
          <div className="bg-white rounded-lg shadow-lg p-6 w-96 relative">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">
              Change Password for:
              <br />
              {selectedTeacher?.username}
              {' | '}
              {selectedTeacher?.firstName} {selectedTeacher?.lastName}
            </h2>

            <label className="block text-gray-700 font-semibold mb-1">
              New Password
            </label>
            <input
              data-testid="new-password-input"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 mb-3"
            />

            <label className="block text-gray-700 font-semibold mb-1">
              Confirm New Password
            </label>
            <input
              data-testid="confirm-password-input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 mb-4"
            />

            {passwordChangeMessage && (
              <div
                data-testid={
                  passwordChangeType === 'success'
                    ? 'password-success-message'
                    : 'password-error-message'
                }
                className={`p-2 mb-3 rounded text-center ${
                  passwordChangeType === 'success'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}
              >
                {passwordChangeMessage}
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                data-testid="password-cancel-btn"
                onClick={() => {
                  setShowPasswordModal(false);
                  setNewPassword('');
                  setConfirmPassword('');
                  setPasswordChangeMessage('');
                  setPasswordChangeType('');
                }}
                className="px-4 py-2 rounded bg-gray-300 text-gray-800 hover:bg-gray-400"
              >
                Cancel
              </button>

              <button
                data-testid="password-save-btn"
                onClick={async () => {
                  if (!newPassword || !confirmPassword) {
                    setPasswordChangeMessage(
                      'Please fill in all fields.',
                    );
                    setPasswordChangeType('error');
                    return;
                  }
                  if (newPassword !== confirmPassword) {
                    setPasswordChangeMessage(
                      'Passwords do not match.',
                    );
                    setPasswordChangeType('error');
                    return;
                  }

                  try {
                    setPasswordChangeLoading(true);
                    setPasswordChangeMessage('');

                    await SchoolServiceAdmin.changeTeacherPassword(
                      selectedTeacher.id,
                      {
                        newPassword,
                        confirmPassword,
                      },
                    );

                    setPasswordChangeMessage(
                      'Password changed successfully!',
                    );
                    setPasswordChangeType('success');
                    setTimeout(() => {
                      setShowPasswordModal(false);
                      setNewPassword('');
                      setConfirmPassword('');
                      setPasswordChangeMessage('');
                    }, 1500);
                  } catch (error) {
                    setPasswordChangeMessage(
                      error.response?.data?.message ||
                        'Failed to change password.',
                    );
                    setPasswordChangeType('error');
                  } finally {
                    setPasswordChangeLoading(false);
                  }
                }}
                disabled={passwordChangeLoading}
                className="px-4 py-2 rounded bg-blue-700 text-white font-semibold hover:bg-blue-800 disabled:bg-gray-500"
              >
                {passwordChangeLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      <hr className="w-960 h-2 mx-auto my-4 bg-cyan-800 border-0 rounded md:my-10 dark:bg-gray-700" />

      <UploadExcel />
      <CreateDeleteAbsenceListForTeacher allTeachers={allTeachers} />

      <hr className="w-960 h-2 mx-auto my-4 bg-cyan-800 border-0 rounded md:my-10 dark:bg-gray-700" />

      <div className="flex items-center justify-center w-full py-4">
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            data-testid="email-sending-toggle"
            className="sr-only peer"
            checked={isEmailSendingEnabled}
            onChange={handleCheckboxChange}
          />
          <div
            className="
        w-11 h-6 
        bg-gray-300 
        rounded-full 
        peer 
        peer-checked:bg-green-500 
        peer-focus:ring-2 
        peer-focus:ring-green-300 
        transition-all duration-300
      "
          ></div>
          <div
            className="
        absolute left-0.5 top-0.5 
        w-5 h-5 
        bg-white 
        rounded-full 
        transition-all duration-300 
        peer-checked:translate-x-5 
        peer-checked:scale-110
        peer-checked:shadow-md
      "
          ></div>
          <span className="ml-3 text-gray-800 font-medium select-none">
            Enable Email Sending
          </span>
        </label>
      </div>

      <div className="inline-flex items-center justify-center w-full mt-6">
        <div className="bg-white border border-gray-200 shadow-lg rounded-2xl p-8 max-w-xl w-full transition-transform duration-200 hover:scale-[1.01]">
          <h2 className="text-2xl font-semibold text-gray-800 text-center mb-6 flex items-center justify-center gap-2">
            <CalendarDaysIcon className="h-6 w-6 text-teal-600" />
            Scheduled Reports
          </h2>

          <div className="space-y-5">
            {/* Bi-weekly Report */}
            <div className="flex items-start gap-4 p-5 rounded-xl bg-teal-50 border border-teal-500 hover:border-teal-200 transition-all hover:shadow-sm">
              <div className="p-3 bg-teal-100 rounded-lg">
                <ClipboardDocumentCheckIcon className="h-6 w-6 text-teal-700" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 text-lg mb-1">
                  Bi-weekly Report
                </h3>
                <p className="text-gray-700 leading-snug">
                  <span className="text-teal-700 font-medium">
                    Every two weeks on Sunday
                  </span>
                  <br />
                  Next report:{' '}
                  <span className="font-semibold text-gray-900">
                    {getNextBiWeeklyReportDate()}
                  </span>{' '}
                  <span className="inline-flex items-center text-gray-500 gap-1">
                    <ClockIcon className="h-4 w-4" /> 5:00 PM
                  </span>
                </p>
              </div>
            </div>

            {/* Monthly Report */}
            <div className="flex items-start gap-4 p-5 rounded-xl bg-indigo-50 border border-indigo-500 hover:border-indigo-200 transition-all hover:shadow-sm">
              <div className="p-3 bg-indigo-100 rounded-lg">
                <DocumentChartBarIcon className="h-6 w-6 text-indigo-700" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 text-lg mb-1">
                  Monthly Report (disabled)
                </h3>
                <p className="text-gray-700 leading-snug">
                  <span className="text-indigo-700 font-medium">
                    1st of each month
                  </span>
                  <br />
                  Next report:{' '}
                  <span className="font-semibold text-gray-900">
                    {getNextMonthlyReportDate()}
                  </span>{' '}
                  <span className="inline-flex items-center text-gray-500 gap-1">
                    <ClockIcon className="h-4 w-4" /> 2:00 AM
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <hr className="w-960 h-2  mx-auto my-4 bg-cyan-800 border-0 rounded md:my-10 dark:bg-gray-700" />

      {/* Student Report Section */}
      <div className="inline-flex items-center justify-center w-full">
        <hr className="w-96 h-1 my-8 bg-cyan-800 border-0 rounded" />
        <div className="absolute px-4 -translate-x-1/2 bg-white left-1/2">
          <h2 className="text-xl font-semibold text-gray-800">
            Generate Student Report
          </h2>
        </div>
      </div>

      <div className="bg-white shadow-lg rounded-lg p-6 my-8 max-w-2xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {/* Student select full width */}
          <div className="md:col-span-3">
            <label className="block text-gray-700 font-semibold mb-2">
              Select Student
            </label>

            <Select
              options={
                allStudents?.map((student) => ({
                  value: student.id,
                  label: `${student.id} - ${student.firstName} ${
                    student.lastName
                  } (${
                    student.lastNameArabic && student.firstNameArabic
                      ? `${student.firstNameArabic} ${student.lastNameArabic}`
                      : '-'
                  })`,
                })) || []
              }
              value={(() => {
                const student = allStudents?.find(
                  (s) => s.id === reportStudentId,
                );
                if (!student) return null;

                return {
                  value: reportStudentId,
                  label: `${reportStudentId} - ${student.firstName} ${student.lastName} (${
                    student.firstNameArabic || '-'
                  } ${student.lastNameArabic || '-'})`,
                };
              })()}
              onChange={(selected) =>
                setReportStudentId(selected?.value || '')
              }
              placeholder="Search and select a student..."
              isClearable
              className="text-gray-800 w-full"
            />
          </div>

          {/* Dates row */}
          <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 font-semibold mb-2">
                From Date
              </label>
              <input
                data-testid="report-from-date-input"
                type="date"
                value={reportFromDate}
                onChange={(e) => setReportFromDate(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">
                To Date
              </label>
              <input
                data-testid="report-to-date-input"
                type="date"
                value={reportToDate}
                onChange={(e) => setReportToDate(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
              />
            </div>
          </div>
        </div>

        {reportStudentId && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
            <p className="text-gray-700">
              <span className="font-semibold">Email:</span>{' '}
              <span className="text-blue-600">
                {getSelectedStudentEmail()}
              </span>
            </p>
          </div>
        )}

        <div className="flex justify-center">
          <button
            onClick={handleGenerateStudentReport}
            disabled={reportLoading}
            className="rounded bg-blue-700 text-white px-8 py-2 font-semibold hover:bg-blue-800 disabled:bg-gray-500"
          >
            {reportLoading
              ? 'Generating...'
              : 'Generate & Send Report'}
          </button>
        </div>

        {reportMessage && (
          <div
            className={`mt-4 p-3 rounded ${
              reportMessageType === 'success'
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
            }`}
          >
            {reportMessage}
          </div>
        )}
      </div>

      <hr className="w-960 h-2  mx-auto my-4 bg-cyan-800 border-0 rounded md:my-10 dark:bg-gray-700" />

      {/* All Students Report Section */}
      <div className="inline-flex items-center justify-center w-full">
        <hr className="w-96 h-1 my-8 bg-cyan-800 border-0 rounded" />
        <div className="absolute px-4 -translate-x-1/2 bg-white left-1/2">
          <h2 className="text-xl font-semibold text-gray-800">
            Generate All Students Report
          </h2>
        </div>
      </div>

      <div className="bg-white shadow-lg rounded-lg p-6 my-8 max-w-2xl mx-auto">
        <p className="text-gray-600 mb-4 text-center">
          Generate a comprehensive report for all students and send it
          to the admin email
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              From Date
            </label>
            <input
              data-testid="all-students-report-from-date"
              type="date"
              value={allStudentsReportFromDate}
              onChange={(e) =>
                setAllStudentsReportFromDate(e.target.value)
              }
              className="w-full border border-gray-300 rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              To Date
            </label>
            <input
              data-testid="all-students-report-to-date"
              type="date"
              value={allStudentsReportToDate}
              onChange={(e) =>
                setAllStudentsReportToDate(e.target.value)
              }
              className="w-full border border-gray-300 rounded px-3 py-2"
            />
          </div>
        </div>

        <div className="flex justify-center">
          <button
            onClick={handleGenerateAllStudentsReport}
            disabled={allStudentsReportLoading}
            className="rounded bg-purple-700 text-white px-8 py-2 font-semibold hover:bg-purple-800 disabled:bg-gray-500"
          >
            {allStudentsReportLoading
              ? 'Generating...'
              : 'Generate & Send to Admin'}
          </button>
        </div>

        {allStudentsReportMessage && (
          <div
            className={`mt-4 p-3 rounded ${
              allStudentsReportMessageType === 'success'
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
            }`}
          >
            {allStudentsReportMessage}
          </div>
        )}
      </div>

      <hr className="w-960 h-2  mx-auto my-4 bg-cyan-800 border-0 rounded md:my-10 dark:bg-gray-700" />

      <div className="container my-8">
        <h1 className="text-2xl font-semibold text-center mb-8">
          School Super Admin Services
        </h1>
        <div className="flex justify-center space-x-4">
          <div
            className="max-w-sm bg-white shadow-lg rounded-lg overflow-hidden transform transition duration-300 hover:scale-105"
            onClick={() => navigate('/superadmin/books-services')}
          >
            <div className="relative">
              <img
                src={booksImage}
                alt="Books Services"
                className="w-full h-48 object-cover"
              />
            </div>
            <div className="p-4">
              <h2 className="text-xl font-semibold text-gray-800 text-center">
                Books
              </h2>
            </div>
          </div>
          <div
            className="max-w-sm bg-white shadow-lg rounded-lg overflow-hidden transform transition duration-300 hover:scale-105"
            onClick={() => navigate('/superadmin/holidays-services')}
          >
            <div className="relative">
              <img
                src={holidaysImage}
                alt="Holidays Services"
                className="w-full h-48 object-cover"
              />
            </div>
            <div className="p-4">
              <h2 className="text-xl font-semibold text-gray-800 text-center">
                Holidays
              </h2>
            </div>
          </div>
          <div
            className="max-w-sm bg-white shadow-lg rounded-lg overflow-hidden transform transition duration-300 hover:scale-105"
            onClick={() =>
              navigate('/superadmin/teachers-sap-services')
            }
          >
            <div className="relative">
              <img
                src={sapImage}
                alt="System Application and Product(SAP) Services"
                className="w-full h-48 object-cover"
              />
            </div>
            <div className="p-4">
              <h2 className="text-xl font-semibold text-gray-800 text-center">
                SAP
              </h2>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchoolSuperAdmin;
